import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useDeviceDetect } from '@hooks/usedevicedetect';
import { CONFIGURE_SEARCH } from '@navigation/constants';

import { useAppDispatch, useAppSelector } from '@hooks/statehooks';

import Typography from '@components/typography';
import { StepButton } from '@components/stepbutton';

import { updateSelectedOptionsParams } from '@store/reducers/vehiclesearchparams';
import { setFinalisedState } from '@store/reducers/finalisedoptions';
import { updateSelectedTab } from '@store/reducers/tabs';
import { useSelectedOptions } from '@hooks/useselectedoptions';

import '@styles/customise/nav.scss';
import { NextChevron } from '@assets/svg/nextchevron';
import { PreviousChevron } from '@assets/svg/previouschevron';
import cn from 'classnames';
import { StepWizardNavProps } from 'interface';
import useToggleLiveChat from '@hooks/usetogglelivechat';

/**
 * The Step Wizard nav component
 *
 * @param stepWizard State Wizard component
 * @param stepOrder Order of the wizard steps
 * @returns The Step Wizard nav component
 */
const Nav: React.FC<StepWizardNavProps> = ({ stepWizard, stepOrder }) => {
  const { isMobile } = useDeviceDetect();

  const { showElement } = useToggleLiveChat();
  const { getNumberOfExtrasSelected, getTotalCostOfExtrasSelected } =
    useSelectedOptions();
  const { vehicleSearchParams, selectedOptions } = useAppSelector(
    (state) => state
  );
  const dispatch = useAppDispatch();

  const fireDatalayerEvent = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'configureOptions',
      category: 'Save Options',
      action: 'click',
      lease: {
        no_of_extras: getNumberOfExtrasSelected(),
        cost_of_extras: getTotalCostOfExtrasSelected(),
      },
    });
  };

  let navigate = useNavigate();
  const handleFinish = () => {
    dispatch(updateSelectedOptionsParams());
    dispatch(
      setFinalisedState({
        colours: selectedOptions.colours,
        options: selectedOptions.options,
      })
    );
    showElement();
    fireDatalayerEvent();

    navigate(CONFIGURE_SEARCH(vehicleSearchParams));
  };

  const handleBack = () => {
    stepWizard.previousStep();
  };

  const handleNext = () => {
    stepWizard.nextStep();
  };

  const handleSelectedOptions = () => {
    dispatch(updateSelectedTab({ name: 'customise', index: 1 }));
  };

  const stepLabel = (order: number) => {
    const step = stepOrder
      ? stepOrder.find((item) => item.order === order)
      : null;
    return step ? step.name : '';
  };

  const previousStepLabel = stepLabel(stepWizard.currentStep - 1);
  const nextStepLabel = () => {
    const nextStepOrder = stepWizard.currentStep + 1;
    return stepLabel(nextStepOrder);
  };

  const previousDirectionLabel = isMobile ? 'prev' : 'previous';

  return (
    <section
      className={cn(
        'customise-nav',
        'wide-component-container',
        isMobile ? 'customise-nav-mobile' : ''
      )}
      data-testid="customise-nav"
      data-cy="customise-nav"
    >
      {stepWizard.currentStep === 1 && <span />}
      {stepWizard.currentStep > 1 && (
        <StepButton
          ButtonIcon={PreviousChevron}
          onClick={handleBack}
          direction={previousDirectionLabel}
          step={previousStepLabel}
          className="customise-nav__previous"
        />
      )}
      {stepWizard.currentStep < stepWizard.totalSteps && (
        <StepButton
          ButtonIcon={NextChevron}
          onClick={handleNext}
          direction="next"
          step={nextStepLabel()}
          className="customise-nav__next"
        />
      )}
      {stepWizard.currentStep === stepWizard.totalSteps && (
        <>
          {isMobile ? (
            <StepButton
              ButtonIcon={NextChevron}
              onClick={handleSelectedOptions}
              direction="next"
              step="Selected Options"
              className="customise-nav__next"
            />
          ) : (
            <div className="customise-nav__finish">
              <button type="button" onClick={handleFinish}>
                <Typography
                  variant="caption"
                  size="rg"
                  weight="semi-bold"
                  colour="alternative"
                >
                  Save options
                </Typography>
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default Nav;
